import type { MorrowAgent } from "@t3tools/client-runtime/state/morrowAgentRuntime";
import { previewToolValue } from "@t3tools/client-runtime/state/morrowAgentRuntime";

import { describeToolCall } from "./chat/workEntryStep";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeToolToken(toolName: string | undefined): string {
  return (toolName ?? "")
    .replace(/^\[subagent\]\s*/i, "")
    .trim()
    .toLowerCase();
}

function parseToolJson(value: unknown): unknown {
  if (value && typeof value === "object") return value;
  const raw = previewToolValue(value, 4000);
  if (!raw || (!raw.startsWith("{") && !raw.startsWith("["))) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function vaultFileNameFromResult(result: unknown): string | null {
  const parsed = parseToolJson(result);
  const record = asRecord(parsed);
  return asTrimmedString(record?.name);
}

function vaultSearchMatchesFromResult(result: unknown): string | null {
  const parsed = parseToolJson(result);
  if (!Array.isArray(parsed)) return null;
  const count = parsed.length;
  if (count === 0) return null;
  const names = parsed
    .map((entry) => asTrimmedString(asRecord(entry)?.name))
    .filter((name): name is string => Boolean(name))
    .slice(0, 2);
  if (names.length > 0) {
    const suffix = count > names.length ? ` · +${count - names.length}` : "";
    return `${names.join(" · ")}${suffix}`;
  }
  return `${count} match${count === 1 ? "" : "es"}`;
}

/** One-line subtitle for grouped tool rows in the Actions panel. */
export function formatToolCallContextBrief(
  call: Pick<MorrowAgent["toolCalls"][number], "toolName" | "input" | "result" | "status">,
): string | null {
  const inputBrief = formatToolCallInputBrief(call);
  if (inputBrief) return inputBrief;
  if (call.status === "running") return null;
  const resultBrief = formatToolCallResultBrief(call);
  if (!resultBrief || resultBrief === "Done" || resultBrief === "Working…") return null;
  return resultBrief;
}

function formatDateRange(input: Record<string, unknown>): string | null {
  const min = asTrimmedString(input.time_min ?? input.timeMin ?? input.start_time);
  const max = asTrimmedString(input.time_max ?? input.timeMax ?? input.end_time);
  if (!min && !max) return null;
  const format = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  if (min && max) return `${format(min)} – ${format(max)}`;
  return min ? `from ${format(min)}` : `until ${format(max!)}`;
}

/** One-line human summary of what was sent to a tool — no raw JSON. */
export function formatToolCallInputBrief(
  call: Pick<MorrowAgent["toolCalls"][number], "toolName" | "input" | "result" | "status">,
): string | null {
  const tool = normalizeToolToken(call.toolName);
  const input = asRecord(call.input);
  if (!input) {
    const raw = previewToolValue(call.input, 80);
    return raw?.startsWith("{") ? null : raw;
  }

  if (tool === "read_vault_item") {
    const fromResult = vaultFileNameFromResult(call.result);
    if (fromResult) return fromResult;
    const itemId = asTrimmedString(input.itemId ?? input.item_id);
    if (itemId) return itemId.length > 28 ? `${itemId.slice(0, 26)}…` : itemId;
  }

  const query = asTrimmedString(input.query ?? input.q ?? input.search_query);
  if (query) return query.length > 80 ? `${query.slice(0, 79)}…` : query;

  const range = formatDateRange(input);
  if (range) return range;

  const calendarId = asTrimmedString(input.calendar_id ?? input.calendarId);
  if (calendarId && calendarId !== "primary") return calendarId;

  const count = input.max_results ?? input.maxResults ?? input.limit;
  if (typeof count === "number" && Number.isFinite(count)) {
    const desc = describeToolCall(call.toolName);
    return `Up to ${count} ${desc.action.toLowerCase()}`;
  }

  return null;
}

/** One-line human summary of a tool result — no raw JSON. */
export function formatToolCallResultBrief(
  call: Pick<MorrowAgent["toolCalls"][number], "toolName" | "status" | "result">,
): string {
  const tool = normalizeToolToken(call.toolName);
  if (call.status === "running") return "Working…";
  if (call.status === "failed") {
    const err = previewToolValue(call.result, 100);
    if (err && !err.startsWith("{")) return err;
    return "Failed";
  }

  const raw = previewToolValue(call.result, 240);
  if (!raw) return "Done";

  if (tool === "read_vault_item") {
    const name = vaultFileNameFromResult(call.result);
    if (name) return name;
  }
  if (tool === "search_vault") {
    const matches = vaultSearchMatchesFromResult(call.result);
    if (matches) return matches;
  }

  const lower = raw.toLowerCase();
  if (lower.includes("no results") || lower.includes("returned no")) return "No results";
  if (lower.includes("search returned no")) return "No matches";

  if (raw.startsWith("{") || raw.startsWith("[")) {
    let parsed: Record<string, unknown> | null = asRecord(call.result);
    if (!parsed) {
      try {
        parsed = asRecord(JSON.parse(raw));
      } catch {
        parsed = null;
      }
    }
    const data = asRecord(parsed?.data);
    const response = asTrimmedString(data?.response_data ?? data?.message);
    if (response && !response.startsWith("{")) {
      const first = response.split(/\n/u)[0]?.trim();
      if (first && first.length <= 100) return first;
    }
    return "Done";
  }

  const firstLine = raw.split(/\r?\n/u).find((line) => line.trim().length > 0) ?? raw;
  return firstLine.length > 100 ? `${firstLine.slice(0, 99)}…` : firstLine;
}
